import { useQueries } from '@tanstack/react-query';
import {
  ArrowLeft,
  Award,
  Check,
  ChevronDown,
  ChevronRight,
  Facebook,
  FlaskConical,
  Flame,
  Heart,
  Leaf,
  Link2,
  Minus,
  Plus,
  Recycle,
  Shield,
  Snowflake,
  Sprout,
  Star,
  Sun,
  Twitter,
  Wheat,
  Droplet,
  type LucideIcon,
} from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';

import { WhatsAppIcon } from '@/components/WhatsAppIcon';
import type { StorefrontProduct } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useAppTabBar } from '@/features/storefront/layouts/chrome/AppTabBar';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { hasMetaContent, isMetaProse, metaTextLines } from '@/features/storefront/layouts/product/metafields';
import { swatchFor } from '@/features/storefront/layouts/product/options';
import { MetaValue } from '@/features/storefront/layouts/product/ProductParts';
import { useProductParts } from '@/features/storefront/parts/context';
import { partOwnsSpacing } from '@/features/storefront/parts/style';
import type { ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { useStorefrontPaths } from '@/features/storefront/paths';
import {
  parseAccordionBody,
  parseHighlightLine,
  partChoice,
  partFlag,
  partNumber,
  type HighlightIcon,
  type PagePart,
} from '@/features/storefront/sections/parts';
import { STOREFRONT_HEADING_STYLE, STOREFRONT_TILE_IMAGE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';
import { formatMoney } from '@/lib/money';
import { whatsAppShareUrl } from '@/lib/whatsapp';

/**
 * The five components a product page has that the buy column never held.
 *
 * Every one of these was markup inside one theme an hour ago - a breadcrumb
 * over the columns, a row of claims beside the price, a payment panel, a share
 * row, a comparison table under the page. They are here because that is what
 * "the page is section-driven" has to mean: a merchant who wants the
 * comparison table above the description, or no breadcrumb at all, moves a row
 * in the tree rather than asking for a theme.
 *
 * Drawn from the shop's own tokens rather than from any theme's classes - the
 * radius, the surface, the primary and the muted are the merchant's palette -
 * so every one of them reads as part of whichever theme is wearing it. What a
 * theme still owns is where they stand: a region, a column, a width. See
 * `parts/PartRegion.tsx`.
 *
 * Kept out of `ProductParts.tsx` for the reason that file is already at its
 * limit: these five carry queries, tables and popovers, and a module a person
 * has to scroll to find the price in is one they stop reading.
 */

/** The margin a part keeps until the merchant chooses one of their own. */
function ownClass(part: PagePart, fallback: string): string | undefined {
  return partOwnsSpacing(part) ? fallback : undefined;
}

/**
 * Where the shopper is, above the product.
 *
 * Two rungs rather than a real ancestry: the API hands a product page its
 * product and nothing about which shelf it was found on, and a breadcrumb that
 * guessed at the department would be wrong for every product filed on two of
 * them. The catalog is a true parent of everything in the shop, and the
 * merchant's own words for both rungs are settings on the component.
 */
export function ProductBreadcrumbsPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const paths = useStorefrontPaths();
  const product = useProductParts();
  const appBar = useAppTabBar();
  const navigate = useNavigate();
  if (!product) return null;

  const home = partChoice(part, 'home_label').trim() || text('product_page.breadcrumb_home');
  const catalog = partChoice(part, 'catalog_label').trim();
  const rungs: ReactNode[] = [
    <Link key="home" to={paths.home()} className="hover:opacity-60">
      {home}
    </Link>,
  ];
  if (catalog) {
    rungs.push(
      <Link key="catalog" to={paths.products()} className="hover:opacity-60">
        {catalog}
      </Link>,
    );
  }

  // With the app bar on, a phone gets what an app has here: one step back,
  // to wherever the shopper came from - or the catalogue, when this product
  // was the first page they opened. Home is a tab of its own down below.
  const back = appBar ? (
    <button
      type="button"
      onClick={() => {
        const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (index > 0) void navigate(-1);
        else void navigate(paths.products());
      }}
      className={cn('flex items-center gap-1.5 font-medium hover:opacity-60 md:hidden', ownClass(part, 'mt-0'))}
    >
      <ArrowLeft className="size-4 shrink-0 rtl-flip" aria-hidden />
      {text('product_page.back')}
    </button>
  ) : null;

  return (
    <>
    {back}
    <nav
      aria-label={text('product_page.breadcrumb')}
      className={cn(appBar && 'hidden md:block', ownClass(part, 'mt-0'))}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {rungs.map((rung, index) => (
          <li key={index} className="flex items-center gap-1.5">
            {index > 0 ? (
              <ChevronRight
                className="size-3.5 shrink-0 rtl-flip"
                aria-hidden
                style={{ color: 'var(--storefront-muted)' }}
              />
            ) : null}
            {rung}
          </li>
        ))}
        {partFlag(part, 'show_current') ? (
          <li className="flex items-center gap-1.5">
            <ChevronRight
              className="size-3.5 shrink-0 rtl-flip"
              aria-hidden
              style={{ color: 'var(--storefront-muted)' }}
            />
            <span aria-current="page" style={{ color: 'var(--storefront-muted)' }}>
              {product.detail.name}
            </span>
          </li>
        ) : null}
      </ol>
    </nav>
    </>
  );
}

/** Badges to a row: all of them up to four, then whichever of three or four leaves no orphan. */
function badgeColumns(count: number): number {
  if (count <= 4) return count;
  return count % 4 === 0 || count % 3 === 1 ? 4 : 3;
}

/** The outline drawn over each label in the "icons" style. */
const HIGHLIGHT_ICON_COMPONENTS: Record<HighlightIcon, LucideIcon> = {
  leaf: Leaf,
  droplet: Droplet,
  flask: FlaskConical,
  heart: Heart,
  sprout: Sprout,
  sun: Sun,
  award: Award,
  shield: Shield,
  check: Check,
  star: Star,
  wheat: Wheat,
  recycle: Recycle,
  flame: Flame,
  snowflake: Snowflake,
};

/**
 * The short claims a shop makes about everything it sells.
 *
 * One per line in the component's own paragraph field, rather than a block
 * editor: three or four words repeated on every product page is a list a
 * merchant types once, and a nested editor for it would be more chrome than
 * content. Blank means the merchant deleted the claims, and the row goes with
 * them.
 */
export function ProductHighlightsPart({ part }: ProductPartProps) {
  const claims = part.body
    .split('\n')
    .map((claim) => claim.trim())
    .filter(Boolean);
  if (claims.length === 0) return null;

  if (partChoice(part, 'style') === 'icons') {
    // Ingredient badges: a fine outline icon over a small uppercase label,
    // between two hairlines. Up to four to a row, in rows that come out even
    // where they can - six as two threes rather than four and an orphan pair -
    // on every screen, since a badge is narrow enough for a phone's quarter.
    return (
      <ul
        data-highlight-style="icons"
        className={cn(ownClass(part, 'mt-6'), 'grid gap-x-3 gap-y-6 border-y py-6')}
        style={{
          borderColor: 'var(--storefront-surface)',
          gridTemplateColumns: `repeat(${badgeColumns(claims.length)}, minmax(0, 1fr))`,
        }}
      >
        {claims.map((claim) => {
          const { icon, label } = parseHighlightLine(claim);
          const Icon = HIGHLIGHT_ICON_COMPONENTS[icon];
          return (
            <li key={claim} className="flex min-w-0 flex-col items-center gap-2.5 text-center">
              <Icon
                aria-hidden
                data-highlight-icon={icon}
                className="size-7 shrink-0"
                strokeWidth={1.4}
                style={{ color: 'var(--storefront-accent)' }}
              />
              <span className="text-[11px] font-semibold uppercase leading-snug tracking-[0.12em] break-words">
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  if (partChoice(part, 'style') === 'ticks') {
    return (
      <ul className={cn(ownClass(part, 'mt-4'), 'space-y-1.5')}>
        {claims.map((claim) => (
          <li key={claim} className="flex items-center gap-2">
            <Check
              className="size-4 shrink-0"
              aria-hidden
              style={{ color: 'var(--storefront-primary)' }}
            />
            {claim}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className={cn(ownClass(part, 'mt-4'), 'flex flex-wrap gap-2')}>
      {claims.map((claim) => (
        <li
          key={claim}
          className="inline-flex items-center rounded-full border px-3 py-1.5 font-semibold"
          style={{
            borderColor: 'var(--storefront-surface)',
            borderRadius: 'var(--storefront-radius)',
          }}
        >
          {claim}
        </li>
      ))}
    </ul>
  );
}

/**
 * What a card is taken with, and what happens to it.
 *
 * The tenders are named rather than drawn, because a row of scheme logos is a
 * row of trademarks - and a component that shipped them would be putting
 * Visa's mark on every shop that draws it, whether or not that shop takes
 * Visa. The merchant types the list, so a shop that takes three things says
 * three things.
 */
export function ProductPaymentPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const [open, setOpen] = useState(false);
  const heading = part.label.trim() || text('product_page.payment_security');
  const note = part.body.trim();
  const methods = partChoice(part, 'methods')
    .split(',')
    .map((method) => method.trim())
    .filter(Boolean);
  if (methods.length === 0 && !note) return null;

  const body = (
    <div
      className="px-5 py-5"
      style={{
        backgroundColor: 'var(--storefront-surface)',
        borderRadius: 'var(--storefront-radius)',
      }}
    >
      {methods.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {methods.map((method) => (
            <li
              key={method}
              className="px-2.5 py-1.5 text-[11px] font-bold tracking-wide uppercase"
              style={{
                backgroundColor: 'var(--storefront-background)',
                borderRadius: 'calc(var(--storefront-radius) / 2)',
              }}
            >
              {method}
            </li>
          ))}
        </ul>
      ) : null}
      {note ? (
        <p
          className={cn('text-[13px] leading-relaxed', methods.length > 0 && 'mt-4')}
          style={{ color: 'var(--storefront-muted)' }}
        >
          {note}
        </p>
      ) : null}
    </div>
  );

  if (!partFlag(part, 'collapsible')) {
    return (
      <div className={ownClass(part, 'mt-6')}>
        <p className="mb-3 text-sm font-semibold">{heading}</p>
        {body}
      </div>
    );
  }

  return (
    <div
      className={cn(ownClass(part, 'mt-6'), 'border-t')}
      style={{ borderColor: 'var(--storefront-surface)' }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center justify-between gap-4 py-5 text-start text-sm font-semibold"
      >
        {heading}
        <span aria-hidden className="shrink-0">
          {open ? <Minus className="size-4" /> : <Plus className="size-4" />}
        </span>
      </button>
      {open ? <div className="pb-6">{body}</div> : null}
    </div>
  );
}

/**
 * Ways to pass the page on, built from wherever the shopper actually is.
 *
 * Read off `window.location` rather than composed from the shop's own address,
 * so a shop reached on its own domain shares that domain rather than the
 * platform's. Server-rendered or in a test there is no location, and each link
 * falls back to nothing rather than to a broken address.
 */
export function ProductSharePart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  if (!product) return null;

  const here = typeof window === 'undefined' ? '' : window.location.href;
  const name = product.detail.name;
  const links = [
    {
      key: 'facebook',
      icon: Facebook,
      label: text('product_page.share_facebook'),
      href: here ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(here)}` : '',
    },
    {
      key: 'x',
      icon: Twitter,
      label: text('product_page.share_x'),
      href: here
        ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(here)}&text=${encodeURIComponent(name)}`
        : '',
    },
    // No merchant number in this one: it is the shopper passing the page to a
    // friend, and WhatsApp asks them who. Which is why it needs no channel to
    // be connected and is offered on every shop.
    {
      key: 'whatsapp',
      icon: WhatsAppIcon,
      label: text('product_page.share_whatsapp'),
      href: here ? whatsAppShareUrl(`${name} ${here}`) : '',
    },
    { key: 'copy_link', icon: Link2, label: text('product_page.open_page'), href: here },
  ].filter((link) => partFlag(part, link.key) && link.href);
  if (links.length === 0) return null;

  const word = part.label.trim() || text('product_page.share');

  return (
    <div className={cn(ownClass(part, 'mt-8'), 'flex items-center gap-3')}>
      <span className="font-semibold">{word}</span>
      {links.map(({ key, icon: Icon, label, href }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="grid size-9 place-items-center rounded-full border transition hover:opacity-60"
          style={{ borderColor: 'var(--storefront-surface)' }}
        >
          <Icon className="size-4" aria-hidden />
          <span className="sr-only">{label}</span>
        </a>
      ))}
    </div>
  );
}

/** One row of the comparison, and how each column answers it. */
interface CompareRow {
  key: string;
  label: string;
  cell: (product: StorefrontProduct) => ReactNode;
}

/**
 * This product beside the ones it could have been.
 *
 * The neighbours are read at full detail rather than from the summaries the
 * related query returns, because two of the rows worth comparing - what it is
 * offered in, and who made it - live on the detail alone. Under the same query
 * key the product page itself uses, so walking into one of these columns is an
 * instant page rather than a second fetch.
 *
 * Renders nothing at all with fewer than two columns: a comparison table with
 * one thing in it is a specification sheet the page already has.
 */
export function ProductComparisonPart({ part }: ProductPartProps) {
  const paths = useStorefrontPaths();
  const text = useShopContent();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const product = useProductParts();
  const slug = product?.detail.slug ?? '';

  const related = useQueries({
    queries: [
      {
        queryKey: [...scope, 'product', slug, 'related'],
        queryFn: () => source.relatedProducts(slug, 12),
        enabled: Boolean(slug),
      },
    ],
  })[0];

  const wanted = Math.max(2, partNumber(part, 'columns') || 4) - 1;
  const neighbours = [
    ...(related.data?.similar ?? []),
    ...(related.data?.also_liked ?? []),
  ].slice(0, wanted);

  const loaded = useQueries({
    queries: neighbours.map((each) => ({
      queryKey: [...scope, 'product', each.slug],
      queryFn: () => source.product(each.slug),
    })),
  });

  if (!product) return null;

  const columns = [
    product.detail,
    ...loaded.map((query) => query.data).filter(Boolean),
  ] as StorefrontProduct[];
  if (columns.length < 2) return null;

  const optionValues = (each: StorefrontProduct) => {
    const values = new Set<string>();
    for (const variant of each.variants) {
      for (const raw of Object.values(variant.options)) {
        const value = String(raw ?? '').trim();
        if (value) values.add(value);
      }
    }
    return [...values];
  };

  const rows: CompareRow[] = [
    {
      key: 'description',
      label: text('product_page.compare_summary'),
      cell: (each) => (
        <span style={{ color: 'var(--storefront-muted)' }}>{each.short_description || '—'}</span>
      ),
    },
    {
      key: 'price',
      label: text('common.price'),
      cell: (each) => (
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="font-bold">{formatMoney(each.price)}</span>
          {each.compare_at_price ? (
            <span className="line-through" style={{ color: 'var(--storefront-muted)' }}>
              {formatMoney(each.compare_at_price)}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'options',
      label: text('product.options'),
      cell: (each) => {
        const values = optionValues(each);
        if (values.length === 0) {
          return <span style={{ color: 'var(--storefront-muted)' }}>—</span>;
        }
        // A dot where the word names a colour and the word itself where it does
        // not: "Sage Cotton" is a swatch, "3 seat" is not.
        return (
          <span className="flex flex-wrap items-center gap-1.5">
            {values.map((value) => {
              const swatch = swatchFor(value);
              return swatch ? (
                <span
                  key={value}
                  title={value}
                  className="size-4 rounded-full border"
                  style={{ backgroundColor: swatch, borderColor: 'rgba(0,0,0,0.14)' }}
                >
                  <span className="sr-only">{value}</span>
                </span>
              ) : (
                <span key={value}>{value}</span>
              );
            })}
          </span>
        );
      },
    },
    { key: 'brand', label: text('search.brand'), cell: (each) => <span>{each.brand || '—'}</span> },
    {
      key: 'availability',
      label: text('product_page.availability'),
      cell: (each) => (
        <span>
          {each.variants.some((variant) => variant.in_stock)
            ? text('product_page.in_stock')
            : text('product.out_of_stock')}
        </span>
      ),
    },
  ];
  const shown = rows.filter((row) => row.key === 'price' || partFlag(part, `show_${row.key}`));

  const heading = part.label.trim() || text('product_page.compare_title');

  return (
    <section className={ownClass(part, 'mt-14')}>
      <h2 className="text-xl sm:text-2xl" style={STOREFRONT_HEADING_STYLE}>
        {heading}
      </h2>
      {/* `relative` is load-bearing rather than decoration. Without a
          containing block of its own, a table wider than this box propagates
          its overflow past the scroller to the document, and the whole page
          scrolls sideways on a phone instead of the table scrolling inside it. */}
      <div className="relative mt-6 overflow-x-auto">
        {/* Fixed rather than auto: left to size itself on content, a column
            whose summary runs to two lines takes width off the one beside it,
            and four photographs that should read as one row end up four
            different sizes. */}
        <table className="w-full min-w-[52rem] table-fixed border-collapse text-sm">
          <caption className="sr-only">{text('product_page.compare_caption', { product: product.detail.name })}</caption>
          <thead>
            <tr>
              <th scope="col" className="w-32" />
              {columns.map((each) => (
                <th key={each.id} scope="col" className="p-3 text-start align-top font-normal">
                  <Link to={paths.product(each.slug)} className="group block">
                    <span
                      className="block aspect-square w-full overflow-hidden"
                      style={{
                        backgroundColor: 'var(--storefront-surface)',
                        borderRadius: 'var(--storefront-radius)',
                      }}
                    >
                      <StorefrontImage
                        url={each.images[0]?.url}
                        sizes={fixedImageSizes(220)}
                        alt={each.name}
                        className={cn(
                          'size-full transition-transform group-hover:scale-105',
                          STOREFRONT_TILE_IMAGE,
                        )}
                      />
                    </span>
                    <span className="mt-3 block text-[13px] font-bold">{each.name}</span>
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr
                key={row.key}
                className="border-t"
                style={{ borderColor: 'var(--storefront-surface)' }}
              >
                <th
                  scope="row"
                  className="p-3 text-start align-top text-[13px] font-semibold"
                  style={{ color: 'var(--storefront-muted)' }}
                >
                  {row.label}
                </th>
                {columns.map((each) => (
                  <td key={each.id} className="p-3 align-top text-[13px]">
                    {row.cell(each)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One accordion panel, ready to draw. */
interface AccordionPanel {
  key: string;
  title: string;
  body: ReactNode;
}

/**
 * Folding panels - the product's own specifications, then the merchant's own
 * notes - grouped in one accordion.
 *
 * The specifications come first and are the product's: each one with a value
 * is a panel named for it, so "Ingredients" written once on the product is a
 * panel on its page without being typed again here. The merchant's panels are
 * the body, written as `# Title` lines with the text under each, which is the
 * same for every product - how to use, storage, a promise.
 *
 * A heading per panel with a button in it, `aria-expanded` and a labelled
 * region: the accordion pattern screen readers announce. Closed panels stay in
 * the page, hidden, so their words are still the page's for a search engine.
 */
export function ProductAccordionPart({ part }: ProductPartProps) {
  const product = useProductParts();
  const baseId = useId();
  const firstOpen = partFlag(part, 'first_open');
  const singleOpen = partFlag(part, 'single_open');
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set(firstOpen ? [0] : []));

  const panels: AccordionPanel[] = [];
  if (partFlag(part, 'include_specs') && product) {
    for (const metafield of product.detail.metafields.filter(hasMetaContent)) {
      const lines = isMetaProse(metafield) ? metaTextLines(metafield.value) : [];
      panels.push({
        key: `spec-${metafield.key}`,
        title: metafield.name,
        body: isMetaProse(metafield) ? (
          lines.map((line, index) => <p key={`${index}-${line}`}>{line}</p>)
        ) : (
          <MetaValue metafield={metafield} />
        ),
      });
    }
  }
  parseAccordionBody(part.body).forEach((item, index) => {
    panels.push({
      key: `own-${index}`,
      title: item.title,
      body: item.body
        .split(/\n\s*\n/)
        .filter((paragraph) => paragraph.trim())
        .map((paragraph, at) => (
          <p key={at} className="whitespace-pre-line">
            {paragraph.trim()}
          </p>
        )),
    });
  });
  if (panels.length === 0) return null;

  const toggle = (index: number) =>
    setOpen((was) => {
      if (was.has(index)) {
        const next = new Set(was);
        next.delete(index);
        return next;
      }
      return singleOpen ? new Set([index]) : new Set([...was, index]);
    });

  return (
    <div
      data-part-accordion=""
      className={cn(ownClass(part, 'mt-6'), 'border-t')}
      style={{ borderColor: 'var(--storefront-surface)' }}
    >
      {panels.map((panel, index) => {
        const expanded = open.has(index);
        const buttonId = `${baseId}-button-${index}`;
        const regionId = `${baseId}-panel-${index}`;
        return (
          <div key={panel.key} className="border-b" style={{ borderColor: 'var(--storefront-surface)' }}>
            <h3 className="m-0">
              <button
                id={buttonId}
                type="button"
                aria-expanded={expanded}
                aria-controls={regionId}
                onClick={() => toggle(index)}
                className="flex w-full items-center justify-between gap-4 py-4 text-start text-sm font-semibold uppercase tracking-[0.08em]"
              >
                <span className="min-w-0 break-words">{panel.title}</span>
                <ChevronDown
                  aria-hidden
                  className={cn('size-4 shrink-0 transition-transform duration-200', expanded && 'rotate-180')}
                />
              </button>
            </h3>
            <div
              id={regionId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!expanded}
              className="space-y-2 pb-5 text-sm leading-relaxed break-words"
              style={{ color: 'var(--storefront-muted)' }}
            >
              {panel.body}
            </div>
          </div>
        );
      })}
    </div>
  );
}
